import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { RobotoButton } from "@/shared/components";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { QualifiedRoboqlQuery } from "@/shared/domain/query";
import { useConvertNlpToRoboql } from "@/shared/domain/query/hooks";

interface AIQueryModalProps {
  open: boolean;
  onClose: () => void;
  onApplyQuery: (query: QualifiedRoboqlQuery) => void;
  orgId: string;
}

export const AIQueryModal: React.FC<AIQueryModalProps> = ({
  open,
  onClose,
  onApplyQuery,
  orgId,
}) => {
  const theme = useTheme();

  const {
    mutateAsync: convertNlpToRoboql,
    isPending,
    error,
  } = useConvertNlpToRoboql();

  const [naturalLanguageQuery, setNaturalLanguageQuery] = React.useState("");
  const [generatedQuery, setGeneratedQuery] =
    React.useState<QualifiedRoboqlQuery | null>(null);

  const handleSubmit = async () => {
    if (!naturalLanguageQuery.trim()) {
      return;
    }

    setGeneratedQuery(null);

    const result: QualifiedRoboqlQuery = await convertNlpToRoboql({
      prompt: naturalLanguageQuery,
      orgId: orgId,
    });
    setGeneratedQuery(result);
  };

  const handleApply = () => {
    if (generatedQuery) {
      onApplyQuery(generatedQuery);
      handleClose();
    }
  };

  const handleClose = () => {
    setNaturalLanguageQuery("");
    setGeneratedQuery(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: theme.zLayer(10) }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span>🔮</span>
          <RobotoTypography variant="h6">AI RoboQL Generator</RobotoTypography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <RobotoTypography variant="body2" color="text.secondary">
            Describe what you&apos;re looking for in natural language, and AI
            will convert it to a RoboQL query.
          </RobotoTypography>

          <TextField
            label="Natural Language Query"
            placeholder="e.g., Find datasets uploaded by sn-1234 containing a .ulg file and a .bag file"
            multiline
            rows={3}
            fullWidth
            value={naturalLanguageQuery}
            onChange={(e) => setNaturalLanguageQuery(e.target.value)}
            disabled={isPending}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void handleSubmit();
              }
            }}
          />

          {isPending && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CircularProgress size={20} />
              <RobotoTypography variant="body2">
                Generating RoboQL query...
              </RobotoTypography>
            </Box>
          )}

          {error && <Alert severity="error">{error.message}</Alert>}

          {generatedQuery && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <RobotoTypography variant="subtitle2">
                Generated RoboQL Query For Target &#39;{generatedQuery.target}
                &#39;
              </RobotoTypography>

              <Box
                sx={{
                  p: 2,
                  backgroundColor: theme.palette.background.default,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <RobotoTypography
                  variant="body2"
                  component="pre"
                  sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                >
                  {generatedQuery.query}
                </RobotoTypography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <RobotoButton
          onClick={handleClose}
          eventName="CancelGenerateAIQueryClicked"
        >
          Cancel
        </RobotoButton>

        <RobotoButton
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={!naturalLanguageQuery.trim() || isPending}
          eventName="GenerateAIQueryClicked"
        >
          {generatedQuery ? "Regenerate Query" : "Generate Query"}
        </RobotoButton>

        {generatedQuery && (
          <RobotoButton
            variant="contained"
            color="primary"
            onClick={handleApply}
            eventName="ApplyGeneratedAIQueryClicked"
          >
            Apply to Search
          </RobotoButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
